/**
 * Inline citation-marker resolution — pure, DOM-free, shared by the chat
 * MarkdownRenderer (and unit-tested without pulling the renderer's CSS /
 * markdown stack into a test).
 */

/** The citation fields a marker can be resolved against. */
export interface MarkerCitation {
  docName: string;
  page: number | null;
  section: string | null;
  text: string;
  score?: number;
  sourceType?: 'library' | 'ai' | 'web';
  /**
   * 1-based position this source had in the list the MODEL was shown — the N
   * an answer's "[Source N]" marker refers to. Stamped server-side BEFORE
   * uncited sources are dropped, so a marker still resolves after filtering.
   */
  sourceIndex?: number;
}

/**
 * Resolve an inline marker token to a 0-based citation index, or null if it is
 * not a citation marker. Handles the numbered form ([Source N]/[N]/【N】) and the
 * tool-name form (【ext.server.action】) a model emits for an MCP/tool result —
 * mapped to the matching live citation by docName ("MCP · <action>").
 */
export function resolveMarkerToCitationIndex(
  part: string,
  citations: ReadonlyArray<MarkerCitation>,
): number | null {
  const num = part.match(/^[[【](?:Source\s*)?(\d+)[\]】]$/i);
  if (num) {
    const n = parseInt(num[1]!, 10);
    // Prefer the server-stamped `sourceIndex`: the marker's N is a position in
    // the list the MODEL was shown, and that list is filtered before it reaches
    // us (uncited Library sources are dropped). Resolving by array position
    // therefore pointed at the wrong document whenever an earlier source was
    // dropped, or fell out of bounds and silently stripped the badge — a claim
    // rendered with no attribution at all.
    const stamped = citations.findIndex((c) => c.sourceIndex === n);
    if (stamped >= 0) return stamped;
    // Older persisted messages carry no sourceIndex — fall back to the
    // positional reading, still bounds-checked (bug #12): a dangling marker
    // must resolve to null so it is stripped, never rendered raw.
    if (citations.some((c) => typeof c.sourceIndex === 'number')) return null;
    const idx = n - 1;
    return idx >= 0 && idx < citations.length ? idx : null;
  }

  // Extract a tool name from ANYWHERE inside the bracket — the 20B improvises
  // markers like 【commentary: ext.acme.get_weather】 or [source: ext.acme.x], not
  // just a bare [ext.acme.x]. Map whatever tool name it contains to its MCP citation.
  const tool = part.match(/[[【][^[\]【】]*\b((?:ext|iq|forge)\.[a-zA-Z][a-zA-Z0-9_.]*)[^[\]【】]*[\]】]/i);
  if (tool) {
    // 'ext.acme.get_weather' → action 'acme.get_weather'; the MCP citation's
    // docName is 'MCP · acme.get_weather'.
    const action = tool[1]!.split('.').slice(1).join('.').toLowerCase();
    if (!action) return null;
    const i = citations.findIndex((c) =>
      (c.docName ?? '').toLowerCase().endsWith(action),
    );
    return i >= 0 ? i : null;
  }

  // '[Web 1]' → the 1st web-tier citation (the model is shown web sources with a
  // web-relative label; the renderer maps it to the matching teal citation).
  const web = part.match(/^[[【]Web\s*(\d+)[\]】]$/i);
  if (web) {
    const n = parseInt(web[1]!, 10);
    let count = 0;
    for (let i = 0; i < citations.length; i++) {
      if (citations[i]!.sourceType === 'web' && ++count === n) return i;
    }
    return null;
  }
  return null;
}
